import AnimatedNumber from "@/components/common/AnimatedNumber";
import LoadingDots from "@/components/common/LoadingDots";

const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

// Full figures up to six digits; above that "1.2M", so three columns fit the
// cell. The full figure is in the column's tooltip.
export const formatStat = (n) => {
  const v = Math.round(Number(n) || 0);
  return v >= 1_000_000 ? compact.format(v) : v.toLocaleString("en-US");
};

/**
 * A borderless table of figures that share a cell: values on top, their names
 * underneath in small muted type, then a percentage line, each in the same
 * column. The layout does not depend on the figures: every slot renders while
 * loading (dots) and when a figure does not apply (a dash), so the cell keeps
 * its size as values arrive or change.
 *
 * columns: [{ label, name? (spoken name, defaults to label), value (number |
 *   null = not applicable), pct?: string, title? }]
 */
export default function StatColumns({ columns, loading, animate = true }) {
  const template = columns
    .map((_, i) => (i === 0 ? "minmax(0,1fr)" : "auto minmax(0,1fr)"))
    .join(" ");
  // One grid row: a slot per column, with a separator slot between columns
  // ("/" on the value row, empty below it) so every row lines up.
  const row = (name, className, render, separator = "") =>
    columns.flatMap((col, i) => [
      ...(i > 0
        ? [
            <span
              key={`${name}-sep-${i}`}
              aria-hidden="true"
              className="self-center text-[10.5px] font-medium leading-[14px] text-(--text-faint)"
            >
              {separator}
            </span>,
          ]
        : []),
      <span
        key={`${name}-${i}`}
        className={`min-w-0 truncate ${className}`}
        title={col.title}
      >
        {render(col)}
      </span>,
    ]);
  return (
    <div
      className="grid gap-x-1.5 tabular-nums"
      style={{ gridTemplateColumns: template }}
    >
      {row(
        "value",
        "text-[12.5px] font-bold leading-[18px] text-(--text-primary)",
        (col) =>
          loading ? (
            <span className="text-(--text-muted)">
              <LoadingDots label={`Loading ${col.name ?? col.label}`} />
            </span>
          ) : col.value === null ? (
            <span className="text-(--text-muted)">—</span>
          ) : (
            <AnimatedNumber
              value={col.value}
              format={formatStat}
              animate={animate}
            />
          ),
        "/",
      )}
      {row(
        "label",
        "text-[10px] font-medium leading-[14px] text-(--text-muted)",
        (col) => col.label,
      )}
      {row(
        "pct",
        "text-[10.5px] font-semibold leading-[14px] text-(--text-secondary)",
        (col) => (loading || !col.pct ? "\u00a0" : col.pct),
      )}
    </div>
  );
}
